import i18n from 'i18next';

/**
 * Generic, user-facing failure texts.
 *
 * Anything shown to a user when a request fails goes through here. Technical detail -
 * the request URL, host, port, status line, timeout value, stack trace or raw response
 * body - stays in the console logs and never reaches the screen.
 */
export type ErrorMessageKind = 'generic' | 'network' | 'timeout' | 'server';

/** English fallbacks, used only before i18n has initialised. */
const ERROR_FALLBACK: Record<ErrorMessageKind, string> = {
    generic: 'Something went wrong. Please try again.',
    network: 'No connection to the server. Please check your internet connection and try again.',
    timeout: 'The request took too long. Please try again.',
    server: 'The service is temporarily unavailable. Please try again later.',
};

export const translateError = (kind: ErrorMessageKind): string => {
    const key = `errors.${kind}`;
    return i18n.exists(key) ? (i18n.t(key) as string) : ERROR_FALLBACK[kind];
};

/**
 * Server-supplied text is only shown when it reads as a message meant for a user -
 * not a stack trace, an HTML error page or a framework default like
 * "Cannot GET /api/v1/find/routes/12".
 */
export const isPresentableMessage = (message: any): message is string =>
    typeof message === 'string'
    && message.trim().length > 0
    && message.length <= 200
    && !/^(Cannot|<|\{|\[)/.test(message.trim())
    && !/https?:\/\/|\/api\/v\d/i.test(message);
